import { useState } from 'react';

import { pluralize } from '@/portainer/helpers/strings';

import { Modal, OnSubmit, ModalType, openModal } from '@@/modals';
import { Button } from '@@/buttons';
import { RadioGroup, RadioGroupOption } from '@@/RadioGroup/RadioGroup';
import { Alert } from '@@/Alert';

import { Environment, KUBESOLO_PROVIDER } from '../types';

type RemoveMode = 'keep' | 'delete';

interface Props {
  onSubmit: OnSubmit<{ deleteClusters: boolean; confirmed: boolean }>;
  environmentsToDelete: Environment[];
}

function ConfirmDeleteEnvironmentsModal({
  onSubmit,
  environmentsToDelete,
}: Props) {
  const [mode, setMode] = useState<RemoveMode>('keep');
  const cloudEnvironments = environmentsToDelete.filter((e) => e.CloudProvider);
  const kubesoloEnvironments = cloudEnvironments.filter(isKubesolo);
  const clusterEnvironments = cloudEnvironments.filter((e) => !isKubesolo(e));
  const deleteClusters = cloudEnvironments.length > 0 && mode === 'delete';

  return (
    <Modal
      onDismiss={() => onSubmit()}
      aria-label="confirm delete environment modal"
    >
      <Modal.Header title="Are you sure?" modalType={ModalType.Destructive} />

      <Modal.Body>
        <div className="flex flex-col gap-3">
          <p className="!mb-0">
            Are you sure you want to disconnect{' '}
            <b>{environmentsToDelete.length}</b>{' '}
            {pluralize(environmentsToDelete.length, 'environment')} and remove
            their configurations from Portainer?
          </p>
          {cloudEnvironments.length > 0 && (
            <RadioGroup
              name="remove-mode"
              selectedOption={mode}
              onOptionChange={setMode}
              groupClassName="flex flex-col gap-2"
              options={removeModeOptions(
                kubesoloEnvironments.length,
                clusterEnvironments.length
              )}
            />
          )}
          {deleteClusters && (
            <DeleteWarning
              kubesoloEnvironments={kubesoloEnvironments}
              clusterEnvironments={clusterEnvironments}
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={() => onSubmit()}
          data-cy="cancel-delete-environments-button"
          color="default"
        >
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit({ deleteClusters, confirmed: true })}
          data-cy="confirm-delete-environments-button"
          color={
            cloudEnvironments.length > 0 && !deleteClusters
              ? 'dangerlight'
              : 'danger'
          }
        >
          {confirmLabel(deleteClusters, clusterEnvironments.length)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function DeleteWarning({
  kubesoloEnvironments,
  clusterEnvironments,
}: {
  kubesoloEnvironments: Environment[];
  clusterEnvironments: Environment[];
}) {
  return (
    <Alert color="warn" title="This action cannot be undone">
      <div className="flex flex-col gap-2">
        {kubesoloEnvironments.length > 0 && (
          <div>
            <p className="!mb-1">
              Uninstalling deletes every workload, volume and setting on{' '}
              {pluralize(
                kubesoloEnvironments.length,
                'this machine',
                'these machines'
              )}
              :
            </p>
            <EnvironmentList
              environments={kubesoloEnvironments}
              detail={(e) => e.CloudProvider?.SSH?.Host}
            />
            <p className="mt-1 !mb-0">
              If a machine can&apos;t be reached, KubeSolo stays on it and the
              error is added to the activity log. Its environment is still
              removed.
            </p>
          </div>
        )}
        {clusterEnvironments.length > 0 && (
          <div>
            <p className="!mb-1">
              Deleting{' '}
              {pluralize(
                clusterEnvironments.length,
                'the cluster',
                'the clusters'
              )}{' '}
              also deletes every workload and volume on{' '}
              {pluralize(clusterEnvironments.length, 'it', 'them')}:
            </p>
            <EnvironmentList environments={clusterEnvironments} />
          </div>
        )}
      </div>
    </Alert>
  );
}

function EnvironmentList({
  environments,
  detail,
}: {
  environments: Environment[];
  detail?: (environment: Environment) => string | undefined;
}) {
  return (
    <ul className="!mb-0 max-h-40 list-disc overflow-y-auto pl-5 break-words">
      {environments.map((e) => (
        <li key={e.Id}>
          <b>{e.Name}</b>
          {detail?.(e) && ` (${detail(e)})`}
        </li>
      ))}
    </ul>
  );
}

function isKubesolo(environment: Environment) {
  return environment.CloudProvider?.Provider === KUBESOLO_PROVIDER;
}

function removeModeOptions(
  kubesoloCount: number,
  clusterCount: number
): Array<RadioGroupOption<RemoveMode>> {
  const keepDetails = [
    kubesoloCount > 0 &&
      `KubeSolo and its workloads keep running on the ${pluralize(kubesoloCount, 'machine')}.`,
    clusterCount > 0 &&
      `${pluralize(clusterCount, 'The remote cluster keeps', 'The remote clusters keep')} running.`,
  ];
  const deleteActions = [
    clusterCount > 0 &&
      `delete the remote ${pluralize(clusterCount, 'cluster')}`,
    kubesoloCount > 0 && 'uninstall KubeSolo',
  ];
  const deleteDetail =
    kubesoloCount > 0 &&
    'Uninstalling runs over SSH and can take a few minutes.';

  return [
    {
      value: 'keep',
      label: (
        <span>
          <span className="font-semibold">Remove from Portainer only.</span>{' '}
          {keepDetails.filter(Boolean).join(' ')}
        </span>
      ),
    },
    {
      value: 'delete',
      label: (
        <span>
          <span className="font-semibold">
            Remove and {deleteActions.filter(Boolean).join(' and ')}.
          </span>{' '}
          {deleteDetail}
        </span>
      ),
    },
  ];
}

function confirmLabel(deleteClusters: boolean, clusterCount: number) {
  if (!deleteClusters) {
    return 'Remove';
  }

  return clusterCount > 0 ? 'Remove and delete' : 'Remove and uninstall';
}

export async function confirmDeleteEnvironments(
  environmentsToDelete: Environment[]
) {
  return openModal(ConfirmDeleteEnvironmentsModal, {
    environmentsToDelete,
  });
}
